import React from "react";
import ReactMarkdown from "react-markdown";
import { ArrowRight, CircleCheck } from "lucide-react";
import {
  OnboardingButton,
  ChatMessage,
  SubscriptionStepCta,
} from "../../services/OnboardingService";

interface ChatBubbleProps {
  message: ChatMessage;
  isCurrent?: boolean;
  onAction?: (action: string, value?: string) => void;
  isActionDisabled?: boolean;
  isLoading?: boolean;
  subscriptionStepCta?: SubscriptionStepCta | null;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  isCurrent = false,
  onAction,
  isActionDisabled = false,
  isLoading = false,
  subscriptionStepCta = null,
}) => {
  const isUser = message.type === "user";
  const isSystem = message.type === "system";
  const isSubscriptionStep = message.metadata?.step === "subscription_creation";

  const renderCheckIcons = (node: React.ReactNode): React.ReactNode => {
    if (typeof node === "string") {
      const parts = node.split(/(✅|✓|✔️)/);
      return parts.map((part, index) => {
        if (part === "✅" || part === "✓" || part === "✔️") {
          return (
            <CircleCheck
              key={`check-${index}`}
              className="inline-block w-4 h-4 text-green-500 dark:text-green-400 mx-0.5 align-middle"
            />
          );
        }
        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
      });
    }

    if (Array.isArray(node)) {
      return node.map((child, index) => (
        <React.Fragment key={`node-${index}`}>
          {renderCheckIcons(child)}
        </React.Fragment>
      ));
    }

    if (React.isValidElement(node)) {
      if (node.type === "code" || node.type === "pre") {
        return node;
      }
      const childProps = node.props as { children?: React.ReactNode };
      if (!childProps.children) {
        return node;
      }
      return React.cloneElement(node, {
        ...childProps,
        children: renderCheckIcons(childProps.children),
      });
    }

    return node;
  };

  // Get button brand color based on action
  const getButtonBrandColor = (action: string) => {
    if (action === "connect_hubspot") {
      return "bg-[#FF7A59] hover:bg-[#FF7A59]/90 text-white";
    }
    if (action === "connect_stripe") {
      return "bg-[#635BFF] hover:bg-[#635BFF]/90 text-white";
    }
    if (action === "enable_revsync") {
      return "bg-green-600 hover:bg-green-700 text-white";
    }
    return null;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-text-secondary dark:text-text-secondaryDark">
        <span className="uppercase tracking-wide">
          {isUser ? "You" : isSystem ? "System" : "RevSync"}
        </span>
        <span>{new Date(message.timestamp).toLocaleString()}</span>
      </div>
      <div
        className={`rounded-lg px-4 py-3 border ${
          isUser
            ? "bg-background-panel dark:bg-background-panelDark border-divider dark:border-divider-dark"
            : "bg-background-default dark:bg-background-dark border-divider dark:border-divider-dark"
        }`}
      >
        <div className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <p className="mb-3 last:mb-0 whitespace-pre-wrap">
                  {renderCheckIcons(children)}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc pl-5 space-y-1">
                  {renderCheckIcons(children)}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal pl-5 space-y-1">
                  {renderCheckIcons(children)}
                </ol>
              ),
              li: ({ children }) => (
                <li className="leading-relaxed">
                  {renderCheckIcons(children)}
                </li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">
                  {renderCheckIcons(children)}
                </strong>
              ),
              em: ({ children }) => (
                <em className="italic">{renderCheckIcons(children)}</em>
              ),
              h1: ({ children }) => (
                <h1 className="text-base font-semibold mb-2">
                  {renderCheckIcons(children)}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-sm font-semibold mb-2">
                  {renderCheckIcons(children)}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-sm font-medium mb-2">
                  {renderCheckIcons(children)}
                </h3>
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
        </div>
      </div>
      {isCurrent &&
        (() => {
          const baseButtons = message.buttons as
            | Array<OnboardingButton & { disabled?: boolean }>
            | undefined;
          const hasSubscriptionPrimary =
            isSubscriptionStep && !!subscriptionStepCta?.primaryCta;
          return (baseButtons?.length ?? 0) > 0 || hasSubscriptionPrimary;
        })() && (
          <div className="flex flex-wrap gap-2">
            {((): Array<OnboardingButton & { disabled?: boolean }> => {
              const baseButtons = message.buttons as
                | Array<OnboardingButton & { disabled?: boolean }>
                | undefined;
              if (!isSubscriptionStep || !subscriptionStepCta?.primaryCta) {
                return baseButtons ?? [];
              }
              const { primaryCta, confirmAllowed } = subscriptionStepCta;
              const isConfirmLike = (label: string, action: string) =>
                action === "confirm_subscription_product" ||
                label.toLowerCase().includes("confirm") ||
                label.toLowerCase().includes("continue");
              const primaryButton: OnboardingButton & { disabled?: boolean } = {
                id: `subscription-cta-${primaryCta.action}`,
                label: primaryCta.label,
                action: primaryCta.action,
                value: primaryCta.value,
                style: "primary",
                disabled: !confirmAllowed,
              };
              const secondaryButtons = (baseButtons ?? [])
                .filter((button) => {
                  if (button.action !== primaryCta.action) return true;
                  if (primaryCta.value === undefined) return false;
                  return button.value !== primaryCta.value;
                })
                .map((button) => {
                  if (
                    !confirmAllowed &&
                    isConfirmLike(button.label, button.action)
                  ) {
                    return { ...button, disabled: true };
                  }
                  return button;
                });
              return [primaryButton, ...secondaryButtons];
            })().map((button: OnboardingButton & { disabled?: boolean }) => {
              const isPrimary = button.style === "primary";
              const isGlobalDisabled = isActionDisabled || button.disabled;
              const brandColor = getButtonBrandColor(button.action);

              // Determine button styling
              let buttonClasses =
                "px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2";

              if (brandColor) {
                buttonClasses += ` ${brandColor}`;
              } else if (isPrimary) {
                buttonClasses +=
                  " bg-white dark:bg-white text-black hover:bg-white/90";
              } else {
                buttonClasses +=
                  " bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark";
              }

              if (isGlobalDisabled) {
                buttonClasses += " opacity-50 cursor-not-allowed";
              }

              // Check if button is a primary CTA button (Use buttons, primary style, or branded buttons)
              const isCTAButton =
                button.label.toLowerCase().startsWith("use") ||
                isPrimary ||
                brandColor !== null;
              // Icon color: green for white buttons, white for branded buttons
              const iconColor = brandColor
                ? "text-white"
                : "text-green-500 dark:text-green-400";

              return (
                <button
                  key={button.id}
                  type="button"
                  disabled={isGlobalDisabled || isLoading}
                  onClick={() => onAction?.(button.action, button.value)}
                  className={buttonClasses}
                >
                  {isCTAButton ? (
                    <ArrowRight className={`w-4 h-4 ${iconColor}`} />
                  ) : null}
                  {button.label}
                </button>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default ChatBubble;
